/**
 * Mail archive — UI layer.
 *
 * A Takeout export runs to gigabytes and holds tens of thousands of messages,
 * so nothing here renders a list of all of them: the archive is indexed, a page
 * at a time is shown, and search filters the index rather than the DOM.
 */

import { indexArchive, readMessageAt, MailError } from './mail-parse.js';

const T = (k, p) => {
  let s = (window.__MA_I18N && window.__MA_I18N[k]) || k;
  if (p) for (const a of Object.keys(p).sort((x, y) => y.length - x.length)) {
    s = s.split(':' + a).join(p[a]);
  }
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function human(bytes) {
  const u = [T('unit_b'), T('unit_kb'), T('unit_mb'), T('unit_gb')];
  let n = Number(bytes) || 0, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n < 10 && i ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}

const PAGE = 50;
/* `index` holds only headers; `ranges` holds byte offsets. A message's body is
 * read from its range when it is shown or searched — see mail-parse.js for why
 * the whole archive is never in memory. `bodies` is a small cache of the ones
 * already read. */
const state = {
  file: null, read: null, ranges: [], index: [], bodies: new Map(),
  view: [], page: 0, query: '', truncated: false, kind: '', deepToken: 0,
};

/** A byte-range reader over the File — nothing here ever holds it whole. */
const readerFor = (file) => async (off, len) => {
  if (off >= file.size) return new Uint8Array(0);
  return new Uint8Array(await file.slice(off, Math.min(file.size, off + len)).arrayBuffer());
};

async function messageAt(i) {
  if (state.bodies.has(i)) return state.bodies.get(i);
  const m = await readMessageAt(state.read, state.ranges[i]);
  // A cap so opening thousands of messages in one session cannot itself become
  // the memory problem this design exists to avoid.
  if (state.bodies.size > 400) state.bodies.clear();
  state.bodies.set(i, m);
  return m;
}

function show(html) { const o = el('ma-out'); o.innerHTML = html; o.hidden = false; }
const card = (tone, title, body, extra = '') =>
  `<div class="ma-card ma-tone-${tone}"><p class="ma-title">${title}</p>
     <p class="ma-body">${body}</p>${extra}</div>`;

async function onFile(file) {
  /* Фарид 05.11.2026: у открывалки единица — ОТКРЫТЫЙ архив, то есть показанный
   * список писем. До этого списывалась только выгрузка .eml/вложений, и почту
   * можно было читать сколько угодно раз в обход стены. Резерв — до чтения,
   * списание — когда список на экране; архив, который не прочёлся, не стоит ничего. */
  const TL = window.__toolLimit;
  const run = TL ? TL.begin('mail-archive', TL.fileKey ? TL.fileKey(file) : null) : null;
  if (TL && !run) return;   // стену показал begin()
  state.file = file;
  state.read = readerFor(file);
  state.bodies.clear();
  show(`<div class="ma-working">${T('reading')}</div>`);
  window.__stage && window.__stage('reading');

  let res;
  try {
    res = await indexArchive(state.read, file.size, {
      onProgress: ({ at, size }) => {
        const w = document.querySelector('.ma-working');
        if (w) w.textContent = T('reading_n', { pct: String(Math.round(at / size * 100)) });
      },
      onIndex: ({ done, total }) => {
        const w = document.querySelector('.ma-working');
        if (w) w.textContent = T('indexing_n', { done: String(done), total: String(total) });
      },
    });
  } catch (err) {
    if (run) run.abort();
    show(card('bad', T('verdict_not_mail'), T('body_not_mail')));
    window.__aeDiagnostic && window.__aeDiagnostic({ issue: (err instanceof MailError ? err.code : 'error') });
    return;
  }

  state.ranges = res.ranges;
  state.index = res.index;
  state.truncated = res.truncated;
  state.kind = res.kind;
  state.query = '';
  state.page = 0;
  state.view = res.index.map((_, i) => i);
  try {
    render();
  } catch (err) {
    if (run) run.abort();
    throw err;
  }
  if (run) run.commit();

  window.__stage && window.__stage('analyzed');
  window.__aeToolComplete && window.__aeToolComplete({
    kind: res.kind, messages: res.index.length,
    withAttachments: res.index.filter((r) => r.hasAttachment).length,
    sizeMB: Math.round(file.size / 1048576),
    truncated: res.truncated });
}

function render() {
  const total = state.index.length;
  /* Counted from the headers, not from parsed bodies: knowing which messages
   * carry an attachment must not cost a pass over the whole archive. */
  const withAtt = state.index.filter((r) => r.hasAttachment).length;

  const rows = [
    [T('stat_messages'), String(total)],
    withAtt ? [T('stat_with_attachments'), String(withAtt)] : null,
    [T('stat_size'), human(state.file.size)],
  ].filter(Boolean).map(([k, v]) => `<tr><th scope="row">${k}</th><td>${v}</td></tr>`).join('');

  const note = state.truncated
    ? `<section class="ma-block ma-note"><p class="ma-body">${T('note_truncated')}</p></section>` : '';

  show(card('ok', T('verdict_read', { n: String(total) }),
      T(state.kind === 'eml' ? 'body_read_eml' : 'body_read'),
      `<div class="ma-actions">
         <button type="button" class="ma-btn" id="ma-save-all">${T('save_all')}</button>
         ${withAtt ? `<button type="button" class="ma-btn ma-btn-ghost" id="ma-save-att">${T('save_attachments')}</button>` : ''}
       </div>`)
    + `<section class="ma-block"><h3>${T('stats_title')}</h3><table class="ma-stats">${rows}</table></section>`
    + note
    + `<div class="ma-search">
         <input type="search" id="ma-q" placeholder="${T('search_placeholder')}" value="${esc(state.query)}"
                autocomplete="off" spellcheck="false">
         <span class="ma-count" id="ma-count"></span>
       </div>
       <div class="ma-list" id="ma-list"></div>
       <div class="ma-more-row"><button type="button" class="ma-btn ma-btn-ghost" id="ma-more" hidden>${T('show_more')}</button></div>`);

  wire();
  renderList();
}

function renderList(append = false) {
  const list = el('ma-list');
  const from = append ? state.page * PAGE : 0;
  if (!append) { list.innerHTML = ''; state.page = 0; }

  const slice = state.view.slice(from, from + PAGE);
  const html = slice.map((i) => {
    const r = state.index[i];
    return `<article class="ma-msg" data-i="${i}">
      <header class="ma-msg-head">
        <p class="ma-subj">${esc(r.subject || T('no_subject'))}</p>
        <p class="ma-meta">${esc(r.from || '—')}${r.date ? ` · ${esc(r.date)}` : ''}</p>
      </header>
      <p class="ma-preview" data-preview="${i}"></p>
      <p class="ma-att" data-atts="${i}"></p>
      <div class="ma-msg-actions">
        <button type="button" class="ma-mini" data-open="${i}">${T('read_full')}</button>
        <button type="button" class="ma-mini" data-eml="${i}">EML</button>
      </div>
    </article>`;
  }).join('');

  list.insertAdjacentHTML('beforeend', html);
  state.page++;
  const more = el('ma-more');
  if (more) more.hidden = state.view.length <= state.page * PAGE;
  const count = el('ma-count');
  if (count) count.textContent = state.query
    ? T('search_found', { n: String(state.view.length), total: String(state.index.length) })
    : '';
  wireList();
  fillPreviews(slice);
}

/**
 * Bodies for the rows on screen, read one range at a time.
 *
 * Fifty small reads instead of one enormous one — and the rows that were never
 * scrolled to cost nothing at all.
 */
async function fillPreviews(indices) {
  for (const i of indices) {
    const holder = document.querySelector(`[data-preview="${i}"]`);
    if (!holder || holder.dataset.done) continue;
    holder.dataset.done = '1';
    let m;
    try { m = await messageAt(i); } catch (_) { continue; }
    const preview = m.text.replace(/\s+/g, ' ').slice(0, 220);
    holder.textContent = preview + (m.text.length > 220 ? '…' : '');
    const atts = document.querySelector(`[data-atts="${i}"]`);
    if (atts && m.attachments.length) {
      atts.innerHTML = m.attachments.map((a, k) =>
        `<button type="button" class="ma-chip" data-att="${i}" data-k="${k}">📎 ${esc(a.name)} · ${human(a.size)}</button>`).join('');
      wireList();
    }
  }
}

/**
 * Search.
 *
 * The headers are already in memory, so senders, subjects and dates filter
 * instantly. Message TEXT is on disk, so it is searched by a second pass that
 * streams the archive and adds matches as it finds them — the alternative
 * being either to hold every body in memory (the thing this design exists to
 * avoid) or to quietly stop searching bodies, which the page promises.
 */
function filter(q) {
  state.query = q;
  const needle = q.trim().toLowerCase();
  const token = ++state.deepToken;                 // invalidates a running pass

  if (!needle) { state.view = state.index.map((_, i) => i); renderList(); return; }

  const shallow = state.index
    .filter((r) => `${r.subject} ${r.from} ${r.to} ${r.date}`.toLowerCase().includes(needle))
    .map((r) => r.i);
  state.view = shallow;
  renderList();

  deepSearch(needle, token, new Set(shallow));
}

async function deepSearch(needle, token, already) {
  const count = el('ma-count');
  for (let i = 0; i < state.index.length; i++) {
    if (token !== state.deepToken) return;         // the query changed
    if (already.has(i)) continue;
    let m;
    try { m = await messageAt(i); } catch (_) { continue; }
    if (!m.text.toLowerCase().includes(needle)) continue;
    already.add(i);
    state.view.push(i);
    state.view.sort((a, b) => a - b);
    if (state.view.length <= state.page * PAGE) renderList();
    if (count) count.textContent = T('search_found', { n: String(state.view.length), total: String(state.index.length) });
    // Yield so typing stays responsive on a large archive.
    if (i % 25 === 0) await new Promise((r) => setTimeout(r, 0));
  }
}

/* An .eml is no longer REBUILT from parsed parts. The byte range is known, so
 * the original message goes out verbatim — a rebuilt one loses the attachments,
 * the HTML alternative and every header we did not think to keep, and a mail
 * client is entitled to notice. */
const safeName = (s, fallback) =>
  (String(s || '').replace(/[\\/:*?"<>|\u0000-\u001F]/g, '_').trim().slice(0, 80) || fallback);

function wire() {
  /* Выгрузки .eml и вложений — часть той же единицы: день списан, когда архив
   * открылся (onFile), поэтому они лимит не трогают (Фарид 05.11.2026). */

  const q = el('ma-q');
  if (q) {
    let t = 0;
    q.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => filter(q.value), 180); });
  }
  const more = el('ma-more');
  if (more) more.addEventListener('click', () => renderList(true));

  const all = el('ma-save-all');
  if (all) all.addEventListener('click', async () => {
    const { buildZip } = await import('../archive/archive-repair-core.js');
    const seen = new Map();
    const items = [];
    /* A zip is assembled in memory, so this one export CAN be as large as the
     * archive. The budget stops the tab dying at 90 % and says how many were
     * left out — the same trade as /disk-recovery's zip route. */
    const BUDGET = 512 << 20;
    let held = 0, dropped = 0;
    for (let i = 0; i < state.ranges.length; i++) {
      let bytes;
      try { bytes = await state.read(state.ranges[i][0], state.ranges[i][1] - state.ranges[i][0]); }
      catch (_) { continue; }
      if (held + bytes.length > BUDGET) { dropped++; continue; }
      held += bytes.length;
      let name = safeName(state.index[i].subject, T('no_subject'));
      const n = (seen.get(name) || 0) + 1;
      seen.set(name, n);
      items.push({ path: `${String(i + 1).padStart(4, '0')}-${name}${n > 1 ? `-${n}` : ''}.eml`, bytes });
    }
    const zip = await buildZip(items);
    window.__downloadFile(new Blob([zip], { type: 'application/zip' }), 'messages.zip');
    if (dropped) window.__toast && window.__toast(T('zip_full', { n: String(dropped) }), 'warning');
    window.__aeToolComplete && window.__aeToolComplete({ export: 'all_eml', messages: items.length, dropped });
  });

  const att = el('ma-save-att');
  if (att) att.addEventListener('click', async () => {
    const { buildZip } = await import('../archive/archive-repair-core.js');
    const seen = new Map();
    const items = [];
    // Only the messages whose headers said they carry one get opened at all.
    for (const r of state.index.filter((x) => x.hasAttachment)) {
      let m;
      try { m = await messageAt(r.i); } catch (_) { continue; }
      for (const a of m.attachments) {
        let name = safeName(a.name, 'attachment');
        const n = (seen.get(name) || 0) + 1;
        seen.set(name, n);
        items.push({ path: n > 1 ? `${r.i + 1}-${name}` : name, bytes: a.bytes });
      }
    }
    const zip = await buildZip(items);
    window.__downloadFile(new Blob([zip], { type: 'application/zip' }), 'attachments.zip');
    window.__aeToolComplete && window.__aeToolComplete({ export: 'attachments', files: items.length });
  });
}

function wireList() {
  document.querySelectorAll('[data-open]').forEach((b) => {
    if (b.dataset.wired) return;
    b.dataset.wired = '1';
    b.addEventListener('click', async () => {
      /* Узлы берём ДО `await`. Чтение письма занимает время, и за него список
       * успевает перерисоваться — тогда кнопка уже отсоединена, `closest()`
       * возвращает null, и обработчик падает
       * `Cannot read properties of null (reading 'querySelector')` (Sentry H4).
       * Соседние обработчики (`data-eml`, `data-att`) читают всё нужное до
       * ожидания и потому целы — здесь порядок был обратный. */
      const art = b.closest('.ma-msg');
      const prev = art && art.querySelector('.ma-preview');
      const m = await messageAt(Number(b.getAttribute('data-open')));
      // Перерисовали, пока читали, — новый список сам покажет тело письма.
      if (!prev || !prev.isConnected) return;
      prev.textContent = m.text || T('empty_body');
      prev.classList.add('ma-full');
      b.remove();
    });
  });
  document.querySelectorAll('[data-eml]').forEach((b) => {
    if (b.dataset.wired) return;
    b.dataset.wired = '1';
    b.addEventListener('click', async () => {
      const i = Number(b.getAttribute('data-eml'));
      /* The ORIGINAL bytes, not a message rebuilt from what we parsed: the
       * range is right there, and handing back exactly what the archive holds
       * is the only version a mail client is guaranteed to agree with. */
      const [from, to] = state.ranges[i];
      const raw = await state.read(from, to - from);
      window.__downloadFile(new Blob([raw], { type: 'message/rfc822' }),
        `${safeName(state.index[i].subject, T('no_subject'))}.eml`);
    });
  });
  document.querySelectorAll('[data-att]').forEach((b) => {
    if (b.dataset.wired) return;
    b.dataset.wired = '1';
    b.addEventListener('click', async () => {
      const m = await messageAt(Number(b.getAttribute('data-att')));
      const a = m.attachments[Number(b.getAttribute('data-k'))];
      if (!a) return;
      window.__downloadFile(new Blob([a.bytes], { type: a.type || 'application/octet-stream' }), safeName(a.name, 'attachment'));
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const zone = el('maDrop');
  if (!zone) return;
  zone.addEventListener('tool-uploader:file', (ev) => {
    const f = ev.detail && (ev.detail.file || (ev.detail.files && ev.detail.files[0]));
    if (f) onFile(f);
  });
});

export { onFile };
